<script setup>
import { computed } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { dateFormat } from 'shared/helpers/timeHelper';
import { useI18n } from 'vue-i18n';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
  isCloning: {
    type: Boolean,
    default: false,
  },
  isExporting: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['delete', 'clone', 'export']);
const { t } = useI18n();

const createdByName = computed(() => {
  const createdBy = props.bot.created_by;
  if (!createdBy) return t('APP_GLOBAL.DELETED_USER');
  return createdBy.available_name ?? createdBy.email ?? '';
});

const updatedByName = computed(() => {
  const updatedBy = props.bot.updated_by;
  if (!updatedBy) return t('APP_GLOBAL.DELETED_USER');
  return updatedBy.available_name ?? updatedBy.email ?? '';
});

const updatedAt = computed(() => {
  return dateFormat(
    new Date(props.bot.updated_at).getTime() / 1000,
    'LLL d, yyyy'
  );
});

const updatedAtWithTime = computed(() => {
  return dateFormat(
    new Date(props.bot.updated_at).getTime() / 1000,
    'LLL d, yyyy hh:mm a'
  );
});
</script>

<template>
  <BaseTableRow :item="bot">
    <template #default>
      <BaseTableCell class="max-w-0 w-full">
        <div class="flex flex-col items-start gap-1">
          <div class="flex items-center gap-2 min-w-0">
            <span class="block text-heading-3 text-n-slate-12 truncate">
              {{ bot.name }}
            </span>
            <template v-if="bot.display_name">
              <div class="w-px h-3 rounded-lg bg-n-weak flex-shrink-0" />
              <span class="text-body-main text-n-slate-11 truncate">
                {{ bot.display_name }}
              </span>
            </template>
          </div>
          <p class="mb-0 text-n-slate-11 text-body-main line-clamp-2">
            {{ bot.description }}
          </p>
        </div>
      </BaseTableCell>

      <BaseTableCell>
        <div class="flex items-center gap-2 min-w-0">
          <Avatar
            :name="createdByName"
            :size="24"
            rounded-full
            class="flex-shrink-0"
          />
          <span class="text-body-main text-n-slate-12 truncate">
            {{ createdByName }}
          </span>
        </div>
      </BaseTableCell>

      <BaseTableCell>
        <div class="flex items-center gap-2 min-w-0">
          <Avatar
            :name="updatedByName"
            :size="24"
            rounded-full
            class="flex-shrink-0"
          />
          <span class="text-body-main text-n-slate-12 truncate">
            {{ updatedByName }}
          </span>
        </div>
      </BaseTableCell>

      <BaseTableCell :title="updatedAtWithTime">
        <span class="text-body-main text-n-slate-12 whitespace-nowrap">
          {{ updatedAt }}
        </span>
      </BaseTableCell>

      <BaseTableCell align="end">
        <div class="flex gap-3 justify-end flex-shrink-0">
          <router-link
            :to="{ name: 'wgpt_bots_edit', params: { botId: bot.id } }"
          >
            <Button
              v-tooltip.top="$t('WGPT_BOTS.LIST.TOOLTIP.SETTINGS')"
              icon="i-woot-edit-pen"
              sm
              slate
            />
          </router-link>
          <Button
            v-tooltip.top="$t('WGPT_BOTS.LIST.TOOLTIP.CLONE')"
            icon="i-woot-clone"
            slate
            sm
            :disabled="isCloning"
            @click="$emit('clone')"
          />
          <Button
            v-tooltip.top="$t('WGPT_BOTS.LIST.TOOLTIP.EXPORT')"
            icon="i-lucide-upload"
            slate
            sm
            :disabled="isExporting"
            :is-loading="isExporting"
            @click="$emit('export')"
          />
          <Button
            v-tooltip.top="$t('WGPT_BOTS.LIST.TOOLTIP.DELETE')"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            @click="$emit('delete')"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
